import { useMemo, useRef, useEffect, useState } from 'react';
import useAdvancedSearchContext from 'components/consumer/consumer-search/advanced-search-next/hooks/useAdvancedSearchContext';
import useUserLocationLoading from 'global-state/userLocationLoading/useUserLocationLoading';
import useProductVariantsToShow from 'hooks/useProductListingData/useProductVariantsToShow';
import { getColorFilterSignature } from 'components/consumer/consumer-search/advanced-search-next/components/AdvancedSearchProductHits/utils';
import useStaticProductsData from './useStaticProductsData';
import addDerivedProductData from '../useProductListingData/helpers/addDerivedProductData';

// `seed` optionally provides server-fetched category data ({ products,
// upholsteryOptions } — RAW GraphQL shape, not derived) used to initialize the
// accumulation so the grid can render at SSR/first paint without waiting for
// the client fetch. Seeded IDs are intentionally NOT marked as fetched: the
// client query still runs and replaces the seeds in place with enriched data
// (location-based pricing etc.). Seeding only happens on mount (state
// initializers); later seed prop changes are ignored.
const useInfiniteStaticProducts = ({
  productIds: allProductIds,
  searchKey,
  seed
}) => {
  const fetchedIdsRef = useRef(new Set());
  const previousProductIdsRef = useRef([]);
  const previousProductIdsSignatureRef = useRef('');
  const previousSearchKeyRef = useRef(searchKey);
  const currentColorFilterSignature = useMemo(() => getColorFilterSignature(searchKey), [searchKey]);
  const previousColorFilterSignatureRef = useRef('');
  const {
    selectedColors
  } = useAdvancedSearchContext();
  const isLocationLoading = useUserLocationLoading();
  const [accumulatedProducts, setAccumulatedProducts] = useState(
  // addDerivedProductData (applied below) requires product.variants, so
  // products without variants cannot be seeded.
  () => seed?.products?.filter(product => product?.variants?.length > 0) ?? []);
  const [accumulatedUpholsteryOptions, setAccumulatedUpholsteryOptions] = useState(() => seed?.upholsteryOptions ?? []);
  const upholsteryLabelFilters = useMemo(() => accumulatedUpholsteryOptions.map(option => option.label), [accumulatedUpholsteryOptions]);
  useEffect(() => {
    const colorFilterSignatureUnchanged = currentColorFilterSignature === previousColorFilterSignatureRef.current;
    if (searchKey !== previousSearchKeyRef.current && colorFilterSignatureUnchanged) {
      // When search term/context changes, clear all accumulated data
      // This ensures no products from the previous search appear in the new results
      fetchedIdsRef.current = new Set();
      setAccumulatedProducts([]);
      setAccumulatedUpholsteryOptions([]);
      previousProductIdsRef.current = [];
      previousProductIdsSignatureRef.current = '';
      previousSearchKeyRef.current = searchKey;
    }
    previousColorFilterSignatureRef.current = currentColorFilterSignature;
  }, [searchKey, currentColorFilterSignature]);
  useEffect(() => {
    const currentIds = allProductIds || [];
    const previousIds = previousProductIdsRef.current || [];
    const currentSignature = currentIds.slice(0, 5).join(',');
    const previousSignature = previousProductIdsSignatureRef.current;
    const firstIdChanged = previousIds.length > 0 && currentIds.length > 0 && String(previousIds[0]) !== String(currentIds[0]);
    const signatureChanged = previousSignature !== '' && currentSignature !== '' && previousSignature !== currentSignature && !currentSignature.startsWith(previousSignature);
    const shouldReset = firstIdChanged || signatureChanged;
    const colorFilterSignatureChanged = currentColorFilterSignature !== previousColorFilterSignatureRef.current;

    // If Algolia returned completely different product IDs (e.g., filters changed significantly),
    // reset the accumulation to prevent showing stale products from the previous result set
    if (shouldReset && colorFilterSignatureChanged) {
      fetchedIdsRef.current = new Set();
      setAccumulatedProducts([]);
      setAccumulatedUpholsteryOptions([]);
    }
    previousProductIdsRef.current = currentIds;
    previousProductIdsSignatureRef.current = currentSignature;
    previousColorFilterSignatureRef.current = currentColorFilterSignature;
  }, [allProductIds, searchKey, currentColorFilterSignature]);
  const newProductIds = useMemo(() => {
    if (!allProductIds?.length) return [];
    const fetchedNormalized = Array.from(fetchedIdsRef.current).map(id => String(id));
    return allProductIds.filter(id_0 => !fetchedNormalized.includes(String(id_0)));
  }, [allProductIds]);
  const shouldFetch = newProductIds.length > 0 && !isLocationLoading;
  const currentData = useStaticProductsData({
    productIds: newProductIds
  }, {
    skip: !shouldFetch
  });
  const newProductIdsRef = useRef(newProductIds);
  newProductIdsRef.current = newProductIds;
  useEffect(() => {
    const products = currentData?.products;
    const upholsteryOptions = currentData?.upholsteryOptions;
    const isLoading = currentData?.loading;
    const newIds = newProductIdsRef.current;
    if (!products && !isLoading) return;
    if (products?.length > 0) {
      // Only mark IDs as fetched if their data actually came back.
      const returnedIds = new Set(products.map(p => String(p.id)));
      newIds.forEach(id_1 => {
        if (returnedIds.has(String(id_1))) {
          fetchedIdsRef.current.add(String(id_1));
        }
      });
      setAccumulatedProducts(prev => {
        // Freshly fetched data supersedes any accumulated entry with the same
        // id — required for seeded products (server payload, no location
        // context) to be replaced by the enriched client data.
        const incomingIds = new Set(products.map(p_0 => String(p_0.id)));
        const kept = prev.filter(p_1 => !incomingIds.has(String(p_1.id)));
        return [...kept, ...products];
      });
      if (upholsteryOptions?.length > 0) {
        setAccumulatedUpholsteryOptions(prev_0 => {
          // only accumulate upholstery option if its not already present
          const existingIds = new Set(prev_0.map(opt => opt.id));
          const newOptions = upholsteryOptions.filter(opt_0 => !existingIds.has(opt_0.id));
          return [...prev_0, ...newOptions];
        });
      }
    }
  }, [currentData?.products, currentData?.upholsteryOptions, currentData?.loading]);
  const finalProducts = useMemo(() => {
    if (!allProductIds?.length || !accumulatedProducts?.length) {
      return accumulatedProducts;
    }

    // Only return products that are in the current allProductIds list
    // This ensures no stale products from previous searches appear
    // when filter state changes and Algolia returns a new set of IDs
    const requestedIdsNormalized = new Set(allProductIds.map(id_2 => String(id_2)));
    return accumulatedProducts.filter(product_0 => {
      const productIdNormalized = String(product_0.id);
      return requestedIdsNormalized.has(productIdNormalized);
    });
  }, [accumulatedProducts, allProductIds]);
  const productsWithAdditionalData = useMemo(() => finalProducts.map(product_1 => addDerivedProductData({
    product: product_1,
    upholsteryOptions: accumulatedUpholsteryOptions
  })), [accumulatedUpholsteryOptions, finalProducts]);
  const colorFiltersArray = useMemo(() => Object.keys(selectedColors).filter(color => selectedColors[color]), [selectedColors]);
  const productsWithVariantsToShow = useProductVariantsToShow({
    colorFilters: colorFiltersArray.length > 0 ? colorFiltersArray : undefined,
    products: productsWithAdditionalData,
    upholsteryLabelFilters,
    skipClearanceQuery: isLocationLoading
  });
  const allNewIdsAccumulated = useMemo(() => {
    if (!newProductIds.length) return true;
    if (currentData?.loading) return false;
    // Query done. Only wait for IDs that the DB actually returned.
    const returnedProducts = currentData?.products;
    if (!returnedProducts?.length) return true;
    const accumulatedIdsSet = new Set(accumulatedProducts.map(p_2 => String(p_2.id)));
    return returnedProducts.every(p_3 => accumulatedIdsSet.has(String(p_3.id)));
  }, [newProductIds.length, currentData?.loading, currentData?.products, accumulatedProducts]);
  return {
    products: productsWithVariantsToShow ?? [],
    loading: currentData?.loading || !allNewIdsAccumulated
  };
};
export default useInfiniteStaticProducts;